import { ArrowDownload20Regular, ZoomIn20Regular, ZoomOut20Regular } from '@fluentui/react-icons';
import cn from 'classnames';
import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import { Button } from '../Button/Button';
import { useFluentMountNode } from '../hooks/useFluentMountNode';
import { Loader } from '../Loader';
import './ImagePreviewOverlay.scss';

export interface ImagePreviewOverlayProps {
  filename: string;
  imageUrl?: string;
  isLoading?: boolean;
  onCloseClick: () => void;
  onDownloadClick?: () => void;
}

const ZOOM_STEP = 0.2;
const ZOOM_MIN = 0.2;
const ZOOM_MAX = 2;

const clampZoom = (zoom: number): number =>
  Math.round(Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, zoom)) * 100) / 100;

/**
 * Full-screen image preview used by the chat attachments and the file lists.
 */
export const ImagePreviewOverlay: React.FC<ImagePreviewOverlayProps> = ({
  filename,
  imageUrl,
  isLoading = false,
  onCloseClick,
  onDownloadClick,
}) => {
  const { t } = useTranslation();
  const mountNode = useFluentMountNode();
  const rootReference = useRef<HTMLDivElement>(null);
  const [zoomLevel, setZoomLevel] = useState<number>(1);
  const [hasError, setHasError] = useState<boolean>(false);
  const [isImageLoaded, setIsImageLoaded] = useState<boolean>(false);

  useEffect(() => {
    setZoomLevel(1);
    setHasError(false);
    setIsImageLoaded(false);
  }, [imageUrl]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onCloseClick();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onCloseClick]);

  useEffect(() => {
    const previouslyFocused = document.activeElement as HTMLElement | null;
    rootReference.current?.focus();

    return () => previouslyFocused?.focus?.();
  }, [mountNode]);

  if (mountNode === null) {
    return null;
  }

  const handleDownloadClick = () => {
    if (onDownloadClick) {
      onDownloadClick();
      return;
    }

    if (!imageUrl) {
      return;
    }

    // `download` only takes effect for same-origin, blob: and data: sources. A
    // cross-origin URL ignores it and falls back to opening the target, where the
    // response's Content-Disposition decides whether it is saved.
    const link = document.createElement('a');
    link.href = imageUrl;
    link.download = filename;
    link.target = '_blank';
    link.rel = 'noopener';
    document.body.append(link);
    link.click();
    link.remove();
  };

  const showLoader = isLoading || (!!imageUrl && !hasError && !isImageLoaded);
  const canZoom = !showLoader && !hasError && !!imageUrl;

  return createPortal(
    <div
      ref={rootReference}
      className="imagePreviewOverlay flex flex-col"
      role="dialog"
      aria-modal="true"
      aria-label={t('translation:imagePreviewOverlay:dialog_label', {
        defaultValue: 'Bildvorschau: {{filename}}',
        filename,
      })}
      tabIndex={-1}
    >
      <button
        type="button"
        className="backdrop"
        aria-label={t('translation:imagePreviewOverlay:close_backdrop_label', {
          defaultValue: 'Vorschau schließen',
        })}
        onClick={onCloseClick}
      />
      <div className="imageToolbar flex justify-end items-center gap-2">
        <span className="filename font-bold truncate">{filename}</span>
        <Button
          icon={<ZoomIn20Regular />}
          className="toolBtn"
          appearance="transparent"
          aria-label={t('translation:imagePreviewOverlay:zoom_in_label', {
            defaultValue: 'Vergrößern',
          })}
          disabled={!canZoom || zoomLevel >= ZOOM_MAX}
          onClick={() => setZoomLevel(clampZoom(zoomLevel + ZOOM_STEP))}
        />
        <Button
          icon={<ZoomOut20Regular />}
          className="toolBtn"
          appearance="transparent"
          aria-label={t('translation:imagePreviewOverlay:zoom_out_label', {
            defaultValue: 'Verkleinern',
          })}
          disabled={!canZoom || zoomLevel <= ZOOM_MIN}
          onClick={() => setZoomLevel(clampZoom(zoomLevel - ZOOM_STEP))}
        />
        <Button
          icon={<ArrowDownload20Regular />}
          className="toolBtn"
          appearance="transparent"
          aria-label={t('translation:imagePreviewOverlay:download_label', {
            defaultValue: 'Herunterladen',
          })}
          disabled={!imageUrl}
          onClick={handleDownloadClick}
        />
        <Button onClick={onCloseClick}>
          {t('translation:imagePreviewOverlay:close', { defaultValue: 'Schließen' })}
        </Button>
      </div>

      {showLoader && <Loader size="lg" inverted />}

      {!isLoading && (hasError || !imageUrl) && (
        <p className="previewError" role="alert">
          {t('translation:imagePreviewOverlay:preview_failed', {
            defaultValue: 'Die Vorschau konnte nicht geladen werden.',
          })}
        </p>
      )}

      {!isLoading && !hasError && imageUrl && (
        <img
          className={cn({ isLoading: !isImageLoaded })}
          src={imageUrl}
          alt={filename}
          style={{ transform: `scale(${zoomLevel})` }}
          onLoad={() => setIsImageLoaded(true)}
          onError={() => setHasError(true)}
        />
      )}
    </div>,
    mountNode,
  );
};
